import React, { useEffect, useRef, useState } from "react";
import { Drawer, Box, CircularProgress } from "@mui/material";
import { allApis } from "../../../request/config";
import apiRequest from "../../../request/apiRequest/apiRequest";
import { ObjectValues } from "../../login";
import { useAppSelecter } from "../../../hooks/storeHooks";
import MobileEmulator from "../../contentFeedback/Preview/MobileEmulator";
import CustomIconButton from "../../../Components/CustomIconButton";

const DRAWER_WIDTH = 500;

const hideButtonCss = {
  background: "#fff",
  position: "absolute",
  bottom: "40px",
  left: "-2px",
  borderTop: "1px solid #C8C8C8",
  borderBottom: "1px solid #C8C8C8",
  borderRadius: "15px 0 0 15px",
  borderLeft: "1px solid #C8C8C8",
  writingMode: "vertical-rl",
  boxShadow: "-4px 0px 6px 0px #00000040",
  padding: "10px 3px 10px",
  minHeight: "90px",
  zIndex: 5,
};

function QuestionDynamicPreviewDrawer({
  handleDialogClose,
  selectedRow,
  open,
  mt = 63.3, // Top offset for layout consistency
}: any) {
  const predata = useRef();
  const [{ questionEditData, load }, setData] = useState<{
    load: boolean;
    questionEditData: ObjectValues;
  }>({ load: true, questionEditData: {} });
  const [selectedLang, setLang] = useState("english");

  const getApi = (id: string) => allApis?.reportQuestion(id);

  const fetchSingleQuestion = async (id: string) => {
    try {
      const response: any = await apiRequest({
        api: getApi(id),
        method: "GET",
      });
      setData({
        load: false,
        questionEditData: response
          ? {
              ...response,
              languages_available:
                response.translation
                  ?.map((s: { language: any }) => s.language)
                  ?.join(",") ?? "english",
            }
          : null,
      });
    } catch (error: any) {
      console.error("API Error:", error);
    }
  };

  const examsData = useAppSelecter((state: ObjectValues) => state.exams);
  const tags_list = examsData.tags_list ?? [];

  useEffect(() => {
    if (selectedRow?.question && predata.current !== selectedRow?.question) {
      setData({
        load: true,
        questionEditData: {},
      });

      predata.current = selectedRow?.question;
      fetchSingleQuestion(selectedRow?.question);
    }else{
        predata.current = undefined
    }
  }, [selectedRow]);

  const formatPayload = (data: any, lang: string) => {
    const newObj: any = {};
    newObj["Question Tags"] = data.tag
      ? tags_list?.filter((item: { id: any }) =>
          data.tag.map((ss: any) => ss.id ?? ss).includes(item.id)
        )
      : [];

    newObj["Explanation"] =
      data.explanation?.translation?.length > 0
        ? data.explanation?.translation?.find(
            (item: { language: any }) => item.language === lang
          )?.text ?? ""
        : data.explanation?.translation?.text ?? "";

    const options = ["A", "B", "C", "D"];
    data.options?.forEach((opt: any, index: number) => {
      const label = `Option ${options[index]}`;
      newObj[label] =
        opt?.translation?.length > 0
          ? opt.translation?.find(
              (item: { language: any }) => item.language === lang
            )?.text ?? ""
          : opt?.translation?.text ?? "";
      if (opt?.is_correct) newObj[`correct_answer`] = { label };
    });

    newObj["Question"] =
      Array.isArray(data.translation) && data.translation?.length > 0
        ? data.translation?.find(
            (item: { language: any }) => item.language === lang
          )?.text ?? ""
        : data?.translation?.text ?? "";

    return newObj;
  };

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={open}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          backgroundColor: "white",
          boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          mt: `${mt}px`,
          height: `calc(100dvh - ${mt}px)`,
          transition: "all 0.3s ease",
          position: "relative",
        },
      }}
    >
      {/* 🟡 Hide button at bottom-left of drawer */}
      <CustomIconButton
        onClick={handleDialogClose}
        sx={hideButtonCss}
        content={"Hide"}
      />

      {/* 🟢 Drawer content */}
      <Box sx={{ flex: 1 }}>
        {load ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <MobileEmulator
            submittedData={formatPayload(questionEditData, selectedLang)}
            languge={{
              languages: questionEditData?.languages_available?.includes(
                "hindi"
              )
                ? ["english", "hindi"]
                : ["english"],
              selectedLang: selectedLang,
              handleQuizLanguageToggled: (val) => setLang(val),
            }}
          />
        )}
      </Box>
    </Drawer>
  );
}

export default QuestionDynamicPreviewDrawer;
